import type { SystemStyleObject } from "@chakra-ui/react"

/**
 * Class applied briefly to an augmentation panel section (and its matching page
 * section) to signal focus after a scroll/hash navigation. It reads as "this is
 * what you're editing" — a distinct provided state rather than a forced hover,
 * so it never implies the pointer is there.
 *
 * Each surface flashes whatever emphasis suits its own containers (the page's
 * cards take the shadow below; the panel's flat rows fill instead), sharing this
 * class and the timings so both halves of the navigation read as one cue.
 */
export const AUGMENTATION_SECTION_FOCUS_CLASS = "augmentation-section-focus"

/** CSS selector matching a section root that currently carries the focus class. */
export const AUGMENTATION_SECTION_FOCUS_SELECTOR = `.${AUGMENTATION_SECTION_FOCUS_CLASS}`

/**
 * Emphasis shadow, as a theme token. Matches the card hover geometry so it
 * can't spill past (and get clipped by) the scroll containers around cards,
 * pills, and panel rows.
 */
export const AUGMENTATION_SECTION_FOCUS_SHADOW = "cardHover"

/** Fades in as the section arrives, then leaves a little quicker. */
export const AUGMENTATION_SECTION_FOCUS_IN_MS = 250
export const AUGMENTATION_SECTION_FOCUS_OUT_MS = Math.round(
  AUGMENTATION_SECTION_FOCUS_IN_MS * 0.7
)

/**
 * Marks a container that lifts when the section around it takes focus. A page
 * surface declares the treatment once via
 * {@link AUGMENTATION_SECTION_FOCUS_TARGET_CSS}; each container only says that
 * it's a target, so nothing has to name the other's class.
 *
 * An attribute rather than a class because it carries which treatment applies,
 * and because it can't be dropped by a `className` that forgets to concatenate.
 */
export const AUGMENTATION_SECTION_FOCUS_TARGET_ATTR =
  "data-augmentation-section-focus"

/**
 * `card` lifts on the emphasis shadow. `surface` also raises the container onto
 * a solid fill, for cards that rest on a tinted background where a shadow alone
 * reads as a smudge.
 */
export type AugmentationSectionFocusTarget = "card" | "surface"

/** Spread onto a container to make it a focus target. */
export function augmentationSectionFocusTarget(
  target: AugmentationSectionFocusTarget = "card"
) {
  return { [AUGMENTATION_SECTION_FOCUS_TARGET_ATTR]: target }
}

/**
 * The page half of the cue. Apply once on a surface root and every focus target
 * beneath it flashes when its section takes {@link
 * AUGMENTATION_SECTION_FOCUS_CLASS}. Declared from the root rather than per
 * container: emphasizing cards individually regressed the Cover Page offers
 * carousel, which measures card heights as they mount.
 *
 * The panel declares its own treatment — its flat rows fill instead of lifting,
 * where a shadow reads as a blurry halo — sharing this class and these timings
 * so both halves of a navigation land as one cue.
 */
export const AUGMENTATION_SECTION_FOCUS_TARGET_CSS = {
  [`& [${AUGMENTATION_SECTION_FOCUS_TARGET_ATTR}]`]: {
    transition: `box-shadow ${AUGMENTATION_SECTION_FOCUS_OUT_MS}ms ease, background-color ${AUGMENTATION_SECTION_FOCUS_OUT_MS}ms ease`
  },
  [`& .${AUGMENTATION_SECTION_FOCUS_CLASS} [${AUGMENTATION_SECTION_FOCUS_TARGET_ATTR}]`]:
    {
      boxShadow: AUGMENTATION_SECTION_FOCUS_SHADOW,
      transition: `box-shadow ${AUGMENTATION_SECTION_FOCUS_IN_MS}ms ease, background-color ${AUGMENTATION_SECTION_FOCUS_IN_MS}ms ease`
    },
  [`& .${AUGMENTATION_SECTION_FOCUS_CLASS} [${AUGMENTATION_SECTION_FOCUS_TARGET_ATTR}="surface"]`]:
    {
      backgroundColor: "white"
    }
} as const satisfies SystemStyleObject
